import React, { JSX } from 'react';
import styled from 'styled-components';
import StyledLink from '@makeship/core/lib/components/Link';
import { P2, Strong } from '../../../Typography';

import { ProductType } from '../../../../types/common';
import { getPDPTrackingLocation, handleSupportLinkTracking } from '../../../../utils/analytics';
import { FaintCaption } from './sharedStyles';

const SpecsList = styled.ul`
  list-style-type: disc;
`;

const SpecsListItem = styled.li`
  list-style-type: disc;
  margin-left: 30px;
`;

const ProductSpecsText = styled(P2)``;

const AccordionList = styled.ul``;

type ApparelSpec = {
  fit: {
    description: string;
    details: string[];
  };
  materials: {
    description: string;
    details: string[];
  };
  care: string[];
};

const APPAREL_SPECS: Partial<Record<ProductType, ApparelSpec>> = {
  [ProductType.Hoodie]: {
    fit: {
      description: 'This hoodie is designed with a relaxed fit for optimal comfort.',
      details: ['Unisex'],
    },
    materials: {
      description: 'Our premium poly-cotton combines lasting durability with everyday comfort and low maintenance.',
      details: [
        '41% cotton, 57% polyester, 2% other fibers (Exclusive of decorative details)',
        'Preshrunk',
        'Fabric color may vary',
      ],
    },
    care: [
      'Wash inside out. Machine wash cold. Tumble dry low. Do not iron appliqué. Do not bleach. Do not dry clean. Wash with like colors. Please follow all care instructions on the garment label.',
    ],
  },
  [ProductType.ZipUpHoodie]: {
    fit: {
      description: 'This zip-up hoodie is designed with a relaxed fit for optimal comfort.',
      details: ['Unisex'],
    },
    materials: {
      description:
        'Our premium poly-cotton blend combines lasting durability with everyday comfort and low maintenance.',
      details: [
        '41% cotton, 57% polyester, 2% other fibers, metal zipper (Exclusive of decorative details)',
        'Preshrunk',
        'Fabric color may vary',
      ],
    },
    care: [
      'Wash inside out. Machine wash cold. Tumble dry low. Do not iron appliqué. Do not bleach. Do not dry clean. Wash with like colors. Please follow all care instructions on the garment label.',
    ],
  },
  [ProductType.Crewneck]: {
    fit: {
      description: 'This knitted crewneck is designed to have an oversized look.',
      details: ['Unisex'],
    },
    materials: {
      description: 'This unisex knitted crewneck is crafted from a durable cotton yarn.',
      details: ['100% cotton yarn (Exclusive of decorative details)', 'Fabric color may vary'],
    },
    care: [
      'Machine wash cold inside out. Do not bleach. Tumble dry low. Do not iron on print. Keep away from fire. Please follow all care instructions on the garment label.',
    ],
  },
  [ProductType.TShirt]: {
    fit: {
      description: 'This t-shirt is designed for a relaxed boxy look.',
      details: ['Unisex'],
    },
    materials: {
      description: 'This unisex t-shirt is made from a premium cotton jersey.',
      details: ['100% cotton (Exclusive of decorative details)', 'Preshrunk', 'Fabric color may vary'],
    },
    care: [
      'Wash inside out. Machine was cold. Tumble dry low. Do not iron appliqué. Do not bleach. Do not dry clean. Wash with like colors. Please follow all care instructions on the garment label.',
    ],
  },
  [ProductType.Sweatpants]: {
    fit: {
      description: 'These sweatpants are designed with a relaxed fit for optimal comfort.',
      details: ['Unisex', 'Interior drawcord at waist'],
    },
    materials: {
      description:
        'Our premium poly-cotton blend combines lasting durability with everyday comfort and low maintenance.',
      details: [
        '41% cotton, 57% polyester, 2% other fibers (Exclusive of decorative details)',
        'Preshrunk',
        'Fabric color may vary',
      ],
    },
    care: [
      'Wash inside out. Machine wash cold. Tumble dry low. Do not iron appliqué. Do not bleach. Do not dry clean. Wash with like colors. Please follow all care instructions on the garment label.',
    ],
  },
  [ProductType.CottonSocks]: {
    fit: {
      description: 'These crew socks feature a ribbed cuff, soft cushioning on the sole, and seamless construction.',
      details: ['Unisex', 'Hits mid-calf'],
    },
    materials: {
      description: 'These unisex crew socks are made from a cotton polyester blend for comfort and durability.',
      details: ['80% cotton, 20% polyester + spandex', 'Preshrunk', 'Fabric color may vary'],
    },
    care: [
      'Machine wash cold. Tumble dry low. Do not bleach. Do not iron. Do not dry clean. Wash with like colors. Please follow all care instructions on the garment label.',
    ],
  },
  [ProductType.TruckerHat]: {
    fit: {
      description: 'This trucker hat features an adjustable snapback closure.',
      details: ['Unisex', 'Curved brim', 'Circumference: 21.2" - 21.6"'],
    },
    materials: {
      description: 'This trucker hat has a structured front with a mesh back for ventilation.',
      details: ['Polyester + sponge', 'Fabric color may vary'],
    },
    care: [
      'Hand wash or spot clean as needed. Do not bleach. Do not iron. Hang to dry. Do not twist or wring. Please follow all care instructions on the garment label.',
    ],
  },
  [ProductType.BallCap]: {
    fit: {
      description: 'This ball cap features an adjustable strap and buckle.',
      details: ['Unisex', 'Curved brim', 'Circumference: 21.2" - 21.6"'],
    },
    materials: {
      description: 'This unisex ball cap is crafted from a cotton twill for breathability.',
      details: ['100% cotton', 'Metal slider', 'Fabric color may vary'],
    },
    care: [
      'Hand wash or spot clean as needed. Do not bleach. Do not iron. Hang to dry. Do not twist or wring. Please follow all care instructions on the garment label.',
    ],
  },
};

const renderApparelDetailsSection = (section: string, description: JSX.Element, materials: string[]) => (
  <>
    <FaintCaption>{section}</FaintCaption>
    <ProductSpecsText>{description}</ProductSpecsText>
    <SpecsList>
      {materials.map((material) => (
        <SpecsListItem key={material}>
          <ProductSpecsText>{material}</ProductSpecsText>
        </SpecsListItem>
      ))}
    </SpecsList>
  </>
);

const renderCareInstructions = (instructions: (string | JSX.Element)[]) => (
  <>
    <FaintCaption>Care Instructions</FaintCaption>
    <AccordionList>
      {instructions.map((instruction) => (
        <ProductSpecsText>{instruction}</ProductSpecsText>
      ))}
    </AccordionList>
  </>
);

const renderDisclaimer = (disclaimer: string) => <ProductSpecsText>{disclaimer}</ProductSpecsText>;

const renderContactMakeshipSupport = (product: Shopify.Product) => (
  <ProductSpecsText>
    Please contact{' '}
    <StyledLink.Primary
      href="https://support.makeship.com/hc/en-us/requests/new"
      onClick={() => handleSupportLinkTracking('open_support_ticket', getPDPTrackingLocation(product), product)}
    >
      Makeship Customer Support
    </StyledLink.Primary>{' '}
    with any questions or if you need assistance.
  </ProductSpecsText>
);

interface ApparelSpecsProps {
  productType: ProductType;
  disclaimer?: string;
  product: Shopify.Product;
}

const ApparelSpecs: React.FC<ApparelSpecsProps> = ({ productType, disclaimer, product }) => {
  const apparelSpec = APPAREL_SPECS[productType];

  if (!apparelSpec) {
    return null;
  }

  return (
    <>
      {renderApparelDetailsSection('Fit', <Strong>{apparelSpec.fit.description}</Strong>, [
        ...apparelSpec.fit.details,
        ...(disclaimer ? [disclaimer] : []),
      ])}
      <br />
      {renderApparelDetailsSection(
        'Materials',
        <Strong>{apparelSpec.materials.description}</Strong>,
        apparelSpec.materials.details,
      )}
      <br />
      {productType === ProductType.Crewneck && !!disclaimer && (
        <>
          {renderDisclaimer(disclaimer)}
          <br />
        </>
      )}
      {renderCareInstructions(apparelSpec.care)}
      <br />
      {renderContactMakeshipSupport(product)}
    </>
  );
};

export default ApparelSpecs;
